package gr.tech.GRAttestation.pages.admin.stores

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ListStyleType
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaFilter
import com.varabyte.kobweb.silk.components.icons.fa.FaPencil
import com.varabyte.kobweb.silk.components.icons.fa.FaRotateLeft
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.focus
import com.varabyte.kobweb.silk.style.toAttrs
import gr.tech.GRAttestation.components.AdminDetailRow
import gr.tech.GRAttestation.components.PaginationControls
import gr.tech.GRAttestation.components.layouts.AdminPageLayout
import gr.tech.GRAttestation.components.modals.PreviewPDFModal
import gr.tech.GRAttestation.components.modals.StoreInputModal
import gr.tech.GRAttestation.components.widgets.*
import gr.tech.GRAttestation.infrastructure.ReceiptGeneratorData
import gr.tech.GRAttestation.infrastructure.api.*
import gr.tech.GRAttestation.models.attestation.AttestationDTO
import gr.tech.GRAttestation.models.stores.STORE_STATUS
import gr.tech.GRAttestation.models.stores.StoreDTO
import gr.tech.GRAttestation.models.storeusers.STORE_USERS_STATUS
import gr.tech.GRAttestation.models.storeusers.StoreUsersDTO
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.navigation.AdminSitePage
import gr.tech.GRAttestation.sections.admin.AdminStoreInvitesPanel
import gr.tech.GRAttestation.sections.admin.AdminStoreUsersPanel
import gr.tech.GRAttestation.sections.tables.AdminStoreAttestationsTable
import gr.tech.GRAttestation.styles.noBorder
import gr.tech.GRAttestation.util.Constants
import gr.tech.GRAttestation.util.Constants.FONT_FAMILY
import gr.tech.GRAttestation.util.TimeUtils
import gr.tech.GRAttestation.util.closeModal
import gr.tech.GRAttestation.util.showBootstrapModal
import gr.tech.models.storeusers.StoreUsersInviteDTO
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Page("{id}")
@Composable
fun AdminStoreDetailsPage() {
    AdminPageLayout() { context ->
        val storeId = context.route.params["id"]
        val edit = context.route.params["edit"]?.toBoolean() ?: false

        if (storeId == null) {
            context.router.navigateTo(AdminSitePage.Projects.route)
            return@AdminPageLayout
        }

        AdminProjectDetailsScreen(context, storeId)
    }
}

@Composable
fun AdminProjectDetailsScreen(context: PageContext, storeId: String) {
    val scope = rememberCoroutineScope()
    val alertManager = remember { AlertManager() }
    alertManager.DisplayAlerts()

    /* Store Data */
    var storeInfo: StoreDTO? by remember { mutableStateOf(null) }
    var storeEditError by remember { mutableStateOf("") }

    /* Attestation Forms Data */
    val attestations = remember { mutableStateListOf<AttestationDTO>() }
    var formsTotalCount by remember { mutableStateOf(0) }
    val pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT
    val totalPages = (formsTotalCount + pageSize - 1) / pageSize
    var currentPage by remember { mutableStateOf(1) }

    var toDeleteAttestation: AttestationDTO? by remember { mutableStateOf(null) }
    var previewAttestationId: Int? by remember { mutableStateOf(null) }

    // Filter Data
    var basicFilters by remember { mutableStateOf(BasicTableFilters()) }
    var selectedDateColumnLabel by remember { mutableStateOf("Created At") }

    /* Store Users */
    val users = remember { mutableStateListOf<StoreUsersDTO>() }
    var usersTotalCount by remember { mutableStateOf(0) }
    var usersCurrentPage by remember { mutableStateOf(1) }
    var usersBasicFilters by remember { mutableStateOf(BasicTableFilters()) }
    var usersSelectedStatus by remember { mutableStateOf(STORE_USERS_STATUS.ACTIVE) }
    var triggerUsersRefresh by remember { mutableStateOf(false) }

    /* Store Invites */
    val invites = remember { mutableStateListOf<StoreUsersInviteDTO>() }
    var invitesTotalCount by remember { mutableStateOf(0) }
    var invitesCurrentPage by remember { mutableStateOf(1) }
    var invitesBasicFilters by remember { mutableStateOf(BasicTableFilters()) }
    var triggerInvitesRefresh by remember { mutableStateOf(false) }

    // Tab state — attestations shown first
    var activeTab by remember { mutableStateOf("attestations") }


    LaunchedEffect(triggerUsersRefresh, usersCurrentPage, usersBasicFilters, usersSelectedStatus) {
        adminFetchStoreUsersByStoreId(
            storeId = storeId.toInt(),
            pageNumber = usersCurrentPage,
            pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT,
            storeStatus = usersSelectedStatus,
            basicFilters = usersBasicFilters,
            onResponse = { response ->
                if (response.success && response.data != null) {
                    users.clear()
                    users.addAll(response.data.items)
                    usersTotalCount = response.data.totalCount
                    if (users.isEmpty()) usersCurrentPage = 1
                } else {
                    alertManager.addAlert(UserAlert(message = "Error loading store users!", status = 0))
                }
            },
            onError = {
                alertManager.addAlert(UserAlert(message = "Error loading store users!", status = 0))
            }
        )
    }

    LaunchedEffect(triggerInvitesRefresh, invitesCurrentPage, invitesBasicFilters) {
        adminFetchStoreUserInvitesByStoreId(
            storeId = storeId.toInt(),
            pageNumber = invitesCurrentPage,
            pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT,
            basicFilters = invitesBasicFilters,
            onResponse = { response ->
                if (response.success && response.data != null) {
                    invites.clear()
                    invites.addAll(response.data.items)
                    invitesTotalCount = response.data.totalCount
                    if (invites.isEmpty()) invitesCurrentPage = 1
                } else {
                    alertManager.addAlert(UserAlert(message = "Error loading invites!", status = 0))
                }
            },
            onError = {
                alertManager.addAlert(UserAlert(message = "Error loading invites!", status = 0))
            }
        )
    }

    LaunchedEffect(Unit, basicFilters) {
        adminFetchStoreById(
            storeId = storeId,
            onError =  {},
            onResponse = {
                if (it.success && it.data != null) {
                    storeInfo = it.data
                } else {
                    alertManager.addAlert(UserAlert(message = "Error loading store info!", status = 0))
                }
            }
        )

        adminFetchAttestationsByStoreID(
            storeId = storeId,
            pageNumber = currentPage,
            pageSize = pageSize,
            basicFilters = basicFilters,
            onResponse = { response ->
                if (response.success && response.data != null) {
                    attestations.clear()
                    attestations.addAll(response.data.items)
                    formsTotalCount = response.data.totalCount
                    if (attestations.isEmpty()) { currentPage = 1 }
                } else {
                    alertManager.addAlert(UserAlert(message = "Error loading store attestations!", status = 0))
                }
            },
            onError = {}
        )
    }

    // Edit Store
    storeInfo?.let { state ->
        StoreInputModal(
            modalId = "AdminStoreEditModal",
            storeInformation = state,
            formError = storeEditError,
            onClose = {
                closeModal("AdminStoreEditModal")
            },
            onSubmit = {
                scope.launch {
                    storeInfo = it
                    storeEditError = " "
                    adminUpdateStore(
                        editedStore = it,
                        onError = {},
                        onSuccess = { apiResponse ->
                            if (apiResponse.success) {
                                alertManager.addAlert(UserAlert(message = "Store Updated!", status = 2))
                                closeModal("AdminStoreEditModal")
                                window.location.reload()
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Updating Store", status = 0))
                                storeEditError = apiResponse.error?.message ?: "Error updating."
                            }
                        },
                    )
                }
            },
        )
    }

    // To Delete Attestation
    ConfirmModal(
        modalId = "adminConfirmDelete",
        confirmMessage = "Are you sure you want to delete this attestation form?",
        onClose = {
            closeModal("adminConfirmDelete")
        },
        onConfirm = {
            toDeleteAttestation?.let {
                scope.launch {
                    adminDeleteAttestationByID(
                        attestationId = toDeleteAttestation!!.id,
                        onResponse = {
                            if (it.success) {
                                alertManager.addAlert(UserAlert(message = "Attestation Form Deleted!", status = 2))
                                attestations.removeAt(attestations.indexOf(toDeleteAttestation))
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Deleting Attestation Form!", status = 0))
                            }
                        },
                        onError = {
                            alertManager.addAlert(UserAlert(message = "Error Deleting Attestation Form!", status = 0))
                        }
                    )
                    closeModal("adminConfirmDelete")
                }
            }
        }
    )

    PreviewPDFModal(
        isOpen = previewAttestationId != null,
        attachmentId = previewAttestationId,
        onClose = { previewAttestationId = null }
    )

    Column(
        modifier = Modifier
            .padding(24.px)
            .backgroundColor(Colors.LightGray)
            .fillMaxSize()
    ) {
        NavBreadcrumb(ctx = context)

        // Top Row Buttons
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        )
        {
            Button(
                attrs = Modifier
                    .classNames("btn", "btn-primary")
                    .onClick {
                        window.location.reload()
                    }
                    .margin(right = 16.px)
                    .toAttrs()
            ) {
                FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                Text("Refresh")
            }

            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {
                        showBootstrapModal("AdminStoreEditModal")
                    }
                    .toAttrs{}
            ) {
                FaPencil(modifier = Modifier.margin(right = 8.px))
                Text("Edit")
            }

        }

        // Main Body of Project Details
        Column(
            modifier = Modifier
                .backgroundColor(Colors.WhiteSmoke)
                .noBorder()
                .borderRadius(8.px)
                .padding(16.px)
                .fillMaxSize(),
            horizontalAlignment = Alignment.Start
        ) {
            storeInfo?.let { storeState ->
                H2() { Text(storeState.storeName) }

                H4() { Text("Location ID: ${storeState.locationId}") }

                Ul(adminDetailListStyle.toAttrs()) {
                    Li() { AdminDetailRow(name = "Store Representative", value = storeState.storeRep) }

                    Li() { AdminDetailRow(name = "Created At", value = TimeUtils.formatDateTime(storeState.createdAt)) }

                    Li() { AdminDetailRow(name = "Created By", value = storeState.createdBy) }

                    Li() { AdminDetailRow(name = "Updated At", value = TimeUtils.formatDateTime(storeState.updatedAt)) }

                    Li() { AdminDetailRow(name = "Status", value = STORE_STATUS.parseStatus(storeState.status).toString()) }
                }

                // Tab Navigation
                Ul(attrs = Modifier.classNames("nav", "nav-tabs").margin(bottom = 16.px).toAttrs()) {
                    Li(attrs = Modifier.classNames("nav-item").toAttrs()) {
                        Button(
                            attrs = Modifier
                                .classNames("nav-link", if (activeTab == "attestations") "active" else "notactive")
                                .onClick { activeTab = "attestations" }
                                .toAttrs()
                        ) { Text("Forms") }
                    }
                    Li(attrs = Modifier.classNames("nav-item").toAttrs()) {
                        Button(
                            attrs = Modifier
                                .classNames("nav-link", if (activeTab == "users") "active" else "notactive")
                                .onClick { activeTab = "users" }
                                .toAttrs()
                        ) { Text("Users") }
                    }
                    Li(attrs = Modifier.classNames("nav-item").toAttrs()) {
                        Button(
                            attrs = Modifier
                                .classNames("nav-link", if (activeTab == "invites") "active" else "notactive")
                                .onClick { activeTab = "invites" }
                                .toAttrs()
                        ) { Text("Invites") }
                    }
                }

                // Attestations Tab
                if (activeTab == "attestations") {

                    // Attestation Form Table Buttons
                    Row(
                        modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
                        horizontalArrangement = Arrangement.Start,
                        verticalAlignment = Alignment.CenterVertically
                    )
                    {
                        Button(
                            attrs = Modifier.classNames("btn", "btn-primary")
                                .margin(right = 16.px)
                                .onClick {}
                                .toAttrs {
                                    attr("data-bs-toggle", "collapse")
                                    attr("data-bs-target", "#storePdfFilters")
                                }
                        ) {
                            FaFilter(modifier = Modifier.margin(right = 8.px))
                            Text("Filters")
                        }

                        Spacer()

                        PaginationControls(
                            currentPage = currentPage,
                            totalPages = totalPages,
                            onPageSelected = { pageNumber ->
                                currentPage = pageNumber
                            }
                        )
                    }

                    // Collapsable Attestation Form Filters Section
                    Column(
                        modifier = Modifier.id("storePdfFilters").classNames("collapse")
                            .margin(top = 8.px, bottom = 16.px)
                            .backgroundColor(Colors.WhiteSmoke)
                            .borderRadius(8.px)
                            .fillMaxWidth()
                    )
                    {
                        Row(
                            modifier = Modifier.fillMaxWidth().gap(16.px),
                            horizontalArrangement = Arrangement.Start,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                                Button(
                                    attrs = Modifier
                                        .classNames("btn", "btn-secondary", "dropdown-toggle")
                                        .id("dateColumnDropdown")
                                        .toAttrs { attr("data-bs-toggle", "dropdown") }
                                ) { Text(selectedDateColumnLabel) }
                                Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                                    Li {
                                        A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                            .onClick {
                                                basicFilters = basicFilters.copy(dateColumn = "createdAt")
                                                selectedDateColumnLabel = "Created At"
                                            }.toAttrs()
                                        ) { Text("Created At") }
                                    }
                                    Li {
                                        A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                            .onClick {
                                                basicFilters = basicFilters.copy(dateColumn = "signDate")
                                                selectedDateColumnLabel = "Sign Date"
                                            }.toAttrs()
                                        ) { Text("Sign Date") }
                                    }
                                }
                            }

                            Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                                Button(
                                    attrs = Modifier
                                        .classNames("btn", if (basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                                        .onClick { basicFilters = basicFilters.copy(isAscending = true) }
                                        .toAttrs()
                                ) { Text("ASCENDING") }
                                Button(
                                    attrs = Modifier
                                        .classNames("btn", if (!basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                                        .onClick { basicFilters = basicFilters.copy(isAscending = false) }
                                        .toAttrs()
                                ) { Text("DESCENDING") }
                            }

                            Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                                Button(
                                    attrs = Modifier
                                        .classNames("btn", if (basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                                        .onClick { basicFilters = basicFilters.copy(isBefore = true) }
                                        .toAttrs()
                                ) { Text("BEFORE") }
                                Button(
                                    attrs = Modifier
                                        .classNames("btn", if (!basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                                        .onClick { basicFilters = basicFilters.copy(isBefore = false) }
                                        .toAttrs()
                                ) { Text("AFTER") }
                            }

                            Input(
                                type = InputType.Date,
                                attrs = Modifier.classNames("form-control").width(200.px)
                                    .toAttrs {
                                        attr("value", basicFilters.dateString)
                                        onChange {
                                            console.log(it.value)
                                            basicFilters = basicFilters.copy(dateString = it.value)
                                        }
                                    }
                            )

                            Spacer()

                            Button(
                                attrs = Modifier.classNames("btn", "btn-secondary")
                                    .onClick {
                                        basicFilters = BasicTableFilters()
                                        selectedDateColumnLabel = "Created At"
                                    }
                                    .toAttrs()
                            ) {
                                FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                                Text("Reset")
                            }
                        }
                    }

                    AdminStoreAttestationsTable(
                        attestations = attestations,
                        onGenReceipts = {
                            ReceiptGeneratorData.attestation = it
                            ReceiptGeneratorData.store = storeState
                            context.router.navigateTo(AdminSitePage.Receipts.passStoreId(storeId))
                        },
                        onDelete = {
                            toDeleteAttestation = it
                            showBootstrapModal("adminConfirmDelete")
                        },
                        onPreview = { previewAttestationId = it.id },
                    )
                }

                // Users Tab
                if (activeTab == "users") {
                    AdminStoreUsersPanel(
                        alertManager = alertManager,
                        users = users,
                        totalCount = usersTotalCount,
                        currentPage = usersCurrentPage,
                        basicFilters = usersBasicFilters,
                        selectedStatus = usersSelectedStatus,
                        onPageChange = { usersCurrentPage = it },
                        onFiltersChange = { usersBasicFilters = it; usersCurrentPage = 1 },
                        onStatusChange = { usersSelectedStatus = it; usersCurrentPage = 1 },
                        onUpdateUser = { dto ->
                            scope.launch {
                                adminUpdateStoreUser(
                                    updatedStoreUsersDTO = dto,
                                    onSuccess = { response ->
                                        if (response.success) {
                                            triggerUsersRefresh = !triggerUsersRefresh
                                            alertManager.addAlert(UserAlert(message = "User Updated.", status = 2))
                                        } else {
                                            alertManager.addAlert(UserAlert(message = response.error?.message ?: "Error Updating User.", status = 0))
                                        }
                                    },
                                    onError = {
                                        alertManager.addAlert(UserAlert(message = "Error Updating User.", status = 0))
                                    }
                                )
                            }
                        },
                        onDeleteUser = { user ->
                            scope.launch {
                                adminDeleteStoreUser(
                                    storeId = storeState.id,
                                    userUuid = user.uuid,
                                    onSuccess = { response ->
                                        if (response.success) {
                                            triggerUsersRefresh = !triggerUsersRefresh
                                            alertManager.addAlert(UserAlert(message = "User deleted.", status = 2))
                                        } else {
                                            alertManager.addAlert(UserAlert(message = response.error?.message ?: "Error deleting user.", status = 0))
                                        }
                                    },
                                    onError = {
                                        alertManager.addAlert(UserAlert(message = "Error deleting user.", status = 0))
                                    }
                                )
                            }
                        },
                        onEditUserStatus = { user, newStatus ->
                            scope.launch {
                                adminUpdateStoreUserStatus(
                                    storeId = storeState.id,
                                    userUuid = user.uuid,
                                    status = newStatus,
                                    onSuccess = { response ->
                                        if (response.success) {
                                            triggerUsersRefresh = !triggerUsersRefresh
                                            alertManager.addAlert(UserAlert(message = "User Status Updated.", status = 2))
                                        } else {
                                            alertManager.addAlert(
                                                UserAlert(
                                                    message = response.error?.message ?: "Error Updating User status.",
                                                    status = 0
                                                )
                                            )
                                        }
                                    },
                                    onError = {
                                        alertManager.addAlert(UserAlert(message = "Error Updating User status.", status = 0))
                                    },
                                )
                            }
                        },
                        filterId = "storeDetailsUserFilters"
                    )
                }

                // Invites Tab
                if (activeTab == "invites") {
                    AdminStoreInvitesPanel(
                        alertManager = alertManager,
                        invites = invites,
                        totalCount = invitesTotalCount,
                        currentPage = invitesCurrentPage,
                        basicFilters = invitesBasicFilters,
                        lockedStoreId = storeState.id,
                        onPageChange = { invitesCurrentPage = it },
                        onFiltersChange = { invitesBasicFilters = it; invitesCurrentPage = 1 },
                        onInviteCreated = { triggerInvitesRefresh = !triggerInvitesRefresh },
                        onCancelInvite = { invite ->
                            scope.launch {
                                adminCancelStoreUserInvite(
                                    storeId = storeState.id,
                                    inviteUuid = invite.uuid,
                                    onSuccess = { response ->
                                        if (response.success) {
                                            triggerInvitesRefresh = !triggerInvitesRefresh
                                            alertManager.addAlert(UserAlert(message = "Invite cancelled.", status = 2))
                                        } else {
                                            alertManager.addAlert(UserAlert(message = response.error?.message ?: "Error cancelling invite.", status = 0))
                                        }
                                    },
                                    onError = {
                                        alertManager.addAlert(UserAlert(message = "Error cancelling invite.", status = 0))
                                    }
                                )
                            }
                        },
                        filterId = "storeDetailsInviteFilters"
                    )
                }

            } ?: loadingSpinnerMaxSize()
        }
    }
}

val adminDetailListStyle = CssStyle {
    base {
        Modifier
            .listStyle(ListStyleType.None)
            .width(500.px)
            .padding(0.px)
            .margin(bottom = 16.px)
    }
}

val adminInputRowStyle = com.varabyte.kobweb.silk.style.CssStyle {
    base {
        Modifier
            .padding(4.px)
            .height(54.px)
            .fillMaxWidth()
    }
}

val adminInputRowLabelStyle = com.varabyte.kobweb.silk.style.CssStyle {
    base {
        Modifier
            .fontSize(1.cssRem)
            .color(Colors.Grey)
    }
}

val adminInputRowNonEditStyle = com.varabyte.kobweb.silk.style.CssStyle {
    base {
        Modifier.Companion
            .noBorder()
            .borderRadius(0.px)
            .borderBottom(1.px, LineStyle.Solid, Colors.Grey)
    }

}

val adminInputRowEditStyle = com.varabyte.kobweb.silk.style.CssStyle {
    focus {
        Modifier.Companion
            .noBorder()
    }
}

val adminInputErrorStyle = com.varabyte.kobweb.silk.style.CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY)
            .fontWeight(FontWeight.Medium)
            .fontSize(0.8.cssRem)
            .color(Colors.Red)
            .height(20.px)
            .fontStyle(FontStyle.Italic)
    }
}
